// A chart's result as a CSV file: the pure half (serialize, and name the file).
// `components/chart-download.tsx` owns the click and the save.
//
// The source is ALWAYS the raw `ChartResult` — `columns` + `rows`, exactly what
// the compiler returned — never a viz-shaped reshape. That is one
// implementation for all eight viz_types rather than eight, and it is also the
// more useful export: a reader downloading a chart wants the numbers behind it,
// not the presentation. So a pivot's fold to "Other" (lib/pivot.ts), a donut's
// top-3 cap, a table's client-side sort and its row collapse
// (lib/table-fold.ts) are all presentation and none of them touch the file.
//
// It is also why a chart whose BODY is withheld still offers a download. The
// withhold (`PivotTruncatedMessage`) exists because `pivotFromResult`
// zero-fills every `(series, bucket)` pair the result never returned, which
// fabricates a $0 bar for real spend in a truncated result — the file has no
// such fabrication in it, every row in it came from the server. The reader
// still sees the card's own "hit the server's row limit" note above it, which
// is the caveat half of this app's caveat-vs-withhold rule.

/** A field must be quoted when it contains the delimiter, a quote, or a line
 *  break (RFC 4180 §2.6), and we also quote on leading/trailing whitespace so a
 *  value that was padded survives a round trip through a spreadsheet. */
const NEEDS_QUOTING = /[",\r\n]|^\s|\s$/;

/** Excel and Google Sheets evaluate a cell beginning with one of these as a
 *  FORMULA rather than text, so a warehouse row carrying buyer- or talent-
 *  authored text (a brief title, a seller name) could execute on open. The
 *  standard neutralizer is a leading apostrophe, which spreadsheets consume as
 *  "treat the rest as text" and every other CSV reader shows verbatim. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** A string that is simply a number needs no formula guard, and prefixing one
 *  would turn a legitimate "-12.5" into "'-12.5" in every reader. Numeric CELLS
 *  arrive as `number` and never reach the guard at all; this is only for a
 *  numeric-looking STRING, which BigQuery does return for NUMERIC/BIGNUMERIC
 *  columns. */
const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;

/** RFC 4180 §2.1: records are CRLF-terminated. A lone LF is what most readers
 *  accept in practice, but CRLF is what the spec says and what Excel wants. */
const CRLF = "\r\n";

/** One cell, serialized and escaped. Exported for its own test: the escaping
 *  rules are where a CSV writer actually goes wrong. */
export function csvCell(value: unknown): string {
  const text = csvText(value);
  const guarded =
    typeof value === "string" && FORMULA_LEAD.test(text) && !PLAIN_NUMBER.test(text)
      ? `'${text}`
      : text;
  return NEEDS_QUOTING.test(guarded) ? `"${guarded.replaceAll('"', '""')}"` : guarded;
}

/** The unescaped text of one cell. `ChartResult.rows` is `unknown[][]` on the
 *  wire because a BigQuery row can hold any JSON scalar, plus a STRUCT/ARRAY
 *  column as an object — which has no CSV form at all, so it goes in as its
 *  JSON rather than as "[object Object]". */
function csvText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    // A cyclic value cannot come off a JSON wire, so this is unreachable in
    // practice — but a serializer that throws mid-file would lose the whole
    // download for one bad cell, and one unreadable cell is the smaller loss.
    return "";
  }
}

/** A header row plus one record per row. Column count is taken from the header,
 *  so a short row is padded and a long one truncated: a ragged CSV is a corrupt
 *  CSV, and `columns` is the shape the server declared. */
export function toCsv(columns: readonly string[], rows: readonly unknown[][]): string {
  const header = columns.map(csvCell).join(",");
  const records = rows.map((row) => columns.map((_col, i) => csvCell(row[i])).join(","));
  return [header, ...records].join(CRLF) + CRLF;
}

/** Longest slug we will put in a filename. Chart names are short, but nothing
 *  on the wire caps `slice_name`, and some filesystems cap a path component
 *  around 255 bytes. */
const MAX_SLUG_LENGTH = 80;

/** A chart name reduced to something every filesystem accepts. Letters and
 *  digits in any script survive (a Hebrew or CJK chart name stays legible);
 *  everything else — including the separators `/` and `:` that would make the
 *  name illegal on Linux/macOS and Windows respectively — collapses to a single
 *  hyphen. */
export function filenameSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, "");
  return slug || "chart";
}

/** `<chart name>-<YYYY-MM-DD-HHMMSS>.csv`.
 *
 *  The timestamp is the result's OWN `computed_at`, in the reader's local clock,
 *  not the moment they clicked: it is what the card's "as of" stamp already
 *  shows them (`computedAtLabel`), and it makes the filename identify the DATA.
 *  Two downloads of an unchanged chart then agree, and a file sitting in a
 *  Downloads folder a week later still says which query run it came from —
 *  neither of which a click time can do.
 *
 *  An unparseable `computed_at` cannot happen on an honest wire, and if it does
 *  the name simply carries no stamp. Substituting `Date.now()` would be worse
 *  than omitting it: it would look like a real data timestamp and be a
 *  different number entirely. */
export function csvFilename(chartName: string, computedAt: string): string {
  const slug = filenameSlug(chartName);
  const stamp = localStamp(computedAt);
  return stamp ? `${slug}-${stamp}.csv` : `${slug}.csv`;
}

function localStamp(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  );
}

/** Excel reads a CSV as the system codepage unless the file opens with a UTF-8
 *  BOM, so a seller name or brief title with any non-ASCII character in it
 *  arrives mojibaked. Every other reader skips a BOM. Kept out of `toCsv` so
 *  the serializer stays a plain string function its test can assert on.
 *  Spelled as the escape, not the literal character, because the literal is
 *  invisible in an editor and reads as a stray empty string. */
export const UTF8_BOM = "\uFEFF";

export const CSV_MIME_TYPE = "text/csv;charset=utf-8";
